import React from "react";
import { Pencil, Pin, Scissors, Trash2 } from "lucide-react";

/**
 * DocsToolbar — Rename · Pin · Split · Delete action bar shown under
 * the sidebar's Files list. Extracted from WorkspacePage.jsx
 * (iter-103 refactor). Pure presentational; every action is delegated
 * back to the parent via props.
 */
export function DocsToolbar({
    activeFile,
    activePage,
    onRename,
    onTogglePin,
    onPinPage,
    pinningSubpage,
    onSplit,
    onDelete,
    splittingIds,
}) {
    const selected = activeFile;
    const isSplitParent =
        !!selected &&
        !selected.parent_upload_id &&
        (selected.split_status === "done") &&
        (selected.page_count || 1) > 1;
    const isPdfFile =
        !!selected &&
        ((selected.content_type || "").toLowerCase() === "application/pdf" ||
            (selected.original_filename || "")
                .toLowerCase()
                .endsWith(".pdf"));
    const needsSplit =
        !!selected &&
        isPdfFile &&
        (selected.page_count || 0) > 1 &&
        !isSplitParent;
    // "Pin" targets the current viewing context: a specific page on an
    // un-split PDF (behaves like the old per-row pin-page shortcut) or
    // the file itself for single-page files / already-split children.
    const pinTargetsPage =
        !!selected && needsSplit && (activePage || 0) > 0;
    const isPinning =
        pinTargetsPage &&
        pinningSubpage &&
        pinningSubpage.fileId === selected.id &&
        pinningSubpage.page === activePage;
    const pinDisabled = !selected || isSplitParent || isPinning;
    const hint = !selected
        ? "Select a file to edit"
        : isSplitParent
            ? `${selected.display_name || selected.original_filename} is split — edit the individual pages below`
            : null;
    const splitting = selected && splittingIds && splittingIds.has(selected.id);
    const btn =
        "h-8 w-8 inline-flex items-center justify-center rounded-sm text-slate-400 hover:text-white hover:bg-white/10 border border-transparent hover:border-white/25 transition-colors disabled:opacity-30 disabled:cursor-not-allowed";
    return (
        <div
            className="mt-2 flex items-center gap-1"
            title={hint || undefined}
            data-testid="docs-toolbar"
        >
            <button
                type="button"
                disabled={!selected || isSplitParent}
                onClick={() => selected && onRename(selected)}
                data-testid="docs-toolbar-rename"
                aria-label="Rename selected file"
                title="Rename selected file"
                className={btn}
            >
                <Pencil className="w-3.5 h-3.5" />
            </button>
            <button
                type="button"
                disabled={pinDisabled}
                onClick={() => {
                    if (!selected) return;
                    if (pinTargetsPage) {
                        onPinPage && onPinPage(selected, activePage);
                    } else {
                        onTogglePin(selected);
                    }
                }}
                data-testid="docs-toolbar-pin"
                aria-label={
                    pinTargetsPage
                        ? `Pin page ${activePage}`
                        : selected?.pinned
                            ? "Unpin selected file"
                            : "Pin selected file"
                }
                title={
                    pinTargetsPage
                        ? `Pin page ${activePage} to the top of the sidebar`
                        : selected?.pinned
                            ? "Unpin selected file"
                            : "Pin selected file to top"
                }
                className={`${btn} ${selected?.pinned ? "text-[#8fa4f0] hover:text-[#8fa4f0]" : ""}`}
            >
                {isPinning ? (
                    <span className="w-3.5 h-3.5 rounded-full border-2 border-[#1a3fd1]/50 border-t-transparent animate-spin" />
                ) : selected?.pinned ? (
                    <Pin className="w-3.5 h-3.5 fill-current" />
                ) : (
                    <Pin className="w-3.5 h-3.5" />
                )}
            </button>
            {needsSplit && (
                <button
                    type="button"
                    disabled={!selected || splitting}
                    onClick={() => selected && onSplit(selected)}
                    data-testid="docs-toolbar-split"
                    aria-label="Split selected PDF into pages"
                    title={`Split ${selected?.page_count || 0} pages into individual files`}
                    className={`${btn} text-[#8fa4f0] hover:text-[#8fa4f0]`}
                >
                    {splitting ? (
                        <span className="w-3.5 h-3.5 rounded-full border-2 border-[#1a3fd1]/50 border-t-transparent animate-spin" />
                    ) : (
                        <Scissors className="w-3.5 h-3.5" />
                    )}
                </button>
            )}
            <button
                type="button"
                disabled={!selected}
                onClick={() => selected && onDelete(selected)}
                data-testid="docs-toolbar-delete"
                aria-label="Delete selected file"
                title="Delete selected file"
                className={`${btn} hover:text-red-400 hover:border-red-500/30 hover:bg-red-500/10`}
            >
                <Trash2 className="w-3.5 h-3.5" />
            </button>
            <div className="ml-auto text-[10px] text-slate-600 truncate max-w-[120px]">
                {selected
                    ? selected.display_name || selected.original_filename
                    : ""}
            </div>
        </div>
    );
}
